import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  ClipboardCheck,
  RefreshCw,
  Building2,
  CheckCircle2,
  FileCheck2,
  Boxes,
  ArrowRight,
  Package,
} from "lucide-react";
import { trpcClient } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatNumber, formatDate } from "@/lib/utils";
import { ProductionThumbnailCell } from "@/components/shared/ProductionThumbnailCell";
import { useCustomerLookup } from "@/hooks/useFKLookup";
import { useHeaderSearch } from "@/hooks/useHeaderSearch";

export function PendingFqcQueueTab() {
  const navigate = useNavigate();
  const { headerSearch } = useHeaderSearch();
  const { options: customerOptions } = useCustomerLookup();
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("ALL");

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: [
      "pending-fqc-queue",
      headerSearch,
      selectedCustomerId === "ALL" ? undefined : selectedCustomerId,
    ],
    queryFn: async () => {
      return await trpcClient.qcFinal.getPendingQueue.query({
        search: headerSearch.trim() || undefined,
        customerId:
          selectedCustomerId === "ALL" ? undefined : selectedCustomerId,
      });
    },
  });

  const items = data?.items || [];
  const metrics = data?.metrics || {
    totalPendingOrders: 0,
    totalPendingQty: 0,
    uniqueProductsCount: 0,
    uniqueCustomersCount: 0,
  };

  const handleInspectSingle = (item: any) => {
    navigate({
      to: "/qc-final/create",
      search: {
        productionOrderId: item.productionOrderId || undefined,
        // Hàng gia công 100% không có lệnh SX: kiểm theo đơn gia công + dòng đơn hàng
        outsourcePoId: item.outsourcePoId || undefined,
        orderDetailId: item.orderDetailId || undefined,
        poCode: item.poCode,
        productId: item.productId,
        productName: item.productName,
        customerName: item.customerName,
        pendingQty: item.pendingQty,
        qtyPlan: item.qtyPlan,
      },
    });
  };

  return (
    <div className="flex flex-col h-full space-y-2">
      {/* 1. Single-Row Inline Metrics Bar (36-38px) */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-gradient-to-r from-emerald-50/80 via-white to-slate-50 border border-emerald-200/80 rounded-md text-xs shadow-xs">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5 font-medium text-emerald-900">
            <ClipboardCheck className="w-3.5 h-3.5 text-emerald-600" />
            <span>Chờ Kiểm FQC (Nghiệm Thu Thành Phẩm):</span>
            <span className="font-bold text-emerald-700 bg-emerald-100/80 px-1.5 py-0.5 rounded-full text-[11px] tabular-nums">
              {formatNumber(metrics.totalPendingOrders)} lệnh
            </span>
          </div>

          <div className="h-3.5 w-px bg-emerald-200 hidden sm:block" />

          <div className="flex items-center gap-1 text-slate-600">
            <Boxes className="w-3.5 h-3.5 text-slate-500" />
            <span>Sản lượng chờ nghiệm thu:</span>
            <span className="font-bold text-slate-900 tabular-nums">
              {formatNumber(metrics.totalPendingQty)}
            </span>
          </div>

          <div className="h-3.5 w-px bg-emerald-200 hidden md:block" />

          <div className="flex items-center gap-1 text-slate-600">
            <Building2 className="w-3.5 h-3.5 text-indigo-500" />
            <span>Khách hàng:</span>
            <span className="font-semibold text-indigo-700 tabular-nums">
              {formatNumber(metrics.uniqueCustomersCount)} KH
            </span>
          </div>

          <div className="h-3.5 w-px bg-emerald-200 hidden lg:block" />

          <div className="flex items-center gap-1 text-slate-600">
            <Package className="w-3.5 h-3.5 text-amber-500" />
            <span>Sản phẩm:</span>
            <span className="font-semibold text-amber-700 tabular-nums">
              {formatNumber(metrics.uniqueProductsCount)} mặt hàng
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => refetch()}
            disabled={isRefetching}
            className="h-6 px-2 text-[11px] text-slate-600 hover:text-slate-900"
          >
            <RefreshCw
              className={`w-3 h-3 mr-1 ${isRefetching ? "animate-spin" : ""}`}
            />
            Làm mới
          </Button>
        </div>
      </div>

      {/* 2. Filter Ribbon */}
      <div className="flex items-center justify-between gap-2 bg-white px-3 py-1.5 rounded-md border border-slate-200 text-xs">
        <div className="flex flex-wrap items-center gap-2.5">
          <label className="text-xs font-medium text-slate-600">Khách hàng:</label>
          <select
            value={selectedCustomerId}
            onChange={(e) => setSelectedCustomerId(e.target.value)}
            className="h-7 px-2 text-xs border border-slate-200 rounded-md bg-slate-50 text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="ALL">Tất cả Khách Hàng ({customerOptions.length})</option>
            {customerOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>

          {headerSearch && (
            <span className="text-xs text-slate-500 italic bg-slate-50 px-2 py-0.5 rounded border border-slate-200">
              Từ khóa tìm kiếm: "{headerSearch}"
            </span>
          )}
        </div>
      </div>

      {/* 3. High-Density Enterprise Data-Grid with Solid Sticky Columns */}
      <div className="border border-slate-200 rounded-md bg-white overflow-hidden shadow-xs flex-1 flex flex-col min-h-0">
        <div className="overflow-auto flex-1 max-h-[calc(100vh-270px)]">
          <Table className="w-full text-xs caption-bottom apv-compact-table min-w-[1000px]">
            <TableHeader className="bg-slate-100/90 sticky top-0 z-20 border-b border-slate-200 text-slate-700">
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[140px] font-semibold text-slate-900 sticky left-0 z-30 bg-slate-100 !bg-white border-r border-slate-200">
                  Lệnh SX / Đơn GC
                </TableHead>
                <TableHead className="font-semibold text-slate-800 min-w-[180px]">
                  Khách Hàng
                </TableHead>
                <TableHead className="w-[120px] font-semibold text-slate-800">
                  Mã Hàng
                </TableHead>
                <TableHead className="font-semibold text-slate-800 min-w-[220px]">
                  Tên Sản Phẩm Thành Phẩm
                </TableHead>
                <TableHead className="w-[100px] font-semibold text-slate-800 text-right">
                  Kế Hoạch
                </TableHead>
                <TableHead className="w-[100px] font-semibold text-slate-800 text-right">
                  Hoàn Thành
                </TableHead>
                <TableHead className="w-[100px] font-semibold text-slate-800 text-right">
                  Đã Nghiệm Thu
                </TableHead>
                <TableHead className="w-[120px] font-semibold text-emerald-900 text-right bg-emerald-50/50">
                  Chờ Kiểm FQC
                </TableHead>
                <TableHead className="w-[110px] font-semibold text-slate-800">
                  Hạn Giao Hàng
                </TableHead>
                <TableHead className="w-[110px] text-center font-semibold text-slate-900 sticky right-0 z-30 bg-slate-100 !bg-white border-l border-slate-200">
                  Thao Tác
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={10} className="h-32 text-center text-slate-400">
                    <RefreshCw className="w-5 h-5 mx-auto animate-spin mb-1 text-slate-400" />
                    Đang tải danh sách hàng đợi FQC...
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="h-32 text-center text-slate-500 bg-slate-50/30"
                  >
                    <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
                    <p className="font-medium text-slate-700">
                      Tuyệt vời! Không có lệnh sản xuất nào đang tồn đọng chờ nghiệm thu FQC.
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Toàn bộ thành phẩm sau đóng gói đã được lập Biên bản KCS thành phẩm đầy đủ.
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item: any) => (
                  <TableRow
                    key={item.id}
                    className="hover:bg-slate-50/80 transition-colors"
                  >
                    {/* Mã LSX (Solid Sticky) */}
                    <TableCell className="px-2 py-1.5 sticky left-0 z-10 bg-white !bg-white border-r border-slate-200 align-middle">
                      <span className="font-mono font-semibold text-indigo-700 text-xs">
                        {item.poCode || "—"}
                      </span>
                      {item.sourceType === "OUTSOURCE" && (
                        <div className="text-[10px] text-purple-700 leading-tight" data-testid="fqc-queue-outsource-tag">Gia công 100%</div>
                      )}
                    </TableCell>

                    {/* Khách Hàng */}
                    <TableCell className="px-2 py-1.5 align-middle">
                      <div className="font-medium text-slate-800 leading-tight">
                        {item.customerName}
                      </div>
                    </TableCell>

                    {/* Mã Hàng */}
                    <TableCell className="px-2 py-1.5 font-mono text-slate-700 align-middle">
                      {item.productCode || "-"}
                    </TableCell>

                    {/* Tên Sản Phẩm */}
                    <TableCell className="px-2 py-1.5 align-middle">
                      <div className="flex items-center gap-2.5">
                        <ProductionThumbnailCell
                          imageUrl={item.productImage || item.product?.image}
                          layoutUrl={item.layoutUrl || item.printLayout?.fileUrl}
                          title={item.productName || item.poCode}
                          subtitle={item.poCode}
                          type="auto"
                          size={32}
                          className="shrink-0"
                        />
                        <div className="font-medium text-slate-900 leading-tight min-w-0 flex-1">
                          {item.productName}
                        </div>
                      </div>
                    </TableCell>

                    {/* Kế Hoạch */}
                    <TableCell className="px-2 py-1.5 text-right font-mono text-slate-600 tabular-nums align-middle">
                      {formatNumber(item.qtyPlan)} {item.unit}
                    </TableCell>

                    {/* Hoàn Thành */}
                    <TableCell className="px-2 py-1.5 text-right font-mono text-slate-700 font-medium tabular-nums align-middle">
                      {formatNumber(item.actualQuantity)} {item.unit}
                    </TableCell>

                    {/* Đã Nghiệm Thu */}
                    <TableCell className="px-2 py-1.5 text-right font-mono text-slate-500 tabular-nums align-middle">
                      {item.inspectedQty > 0 ? (
                        <span className="text-emerald-600 font-medium">
                          {formatNumber(item.inspectedQty)}
                        </span>
                      ) : (
                        "-"
                      )}
                    </TableCell>

                    {/* Chờ Kiểm FQC */}
                    <TableCell className="px-2 py-1.5 text-right font-mono font-bold text-emerald-700 bg-emerald-50/50 tabular-nums align-middle">
                      {formatNumber(item.pendingQty)} {item.unit}
                    </TableCell>

                    {/* Hạn Giao Hàng */}
                    <TableCell className="px-2 py-1.5 text-slate-600 align-middle">
                      {formatDate(item.deadline)}
                    </TableCell>

                    {/* Action (Solid Sticky) */}
                    <TableCell className="px-2 py-1 text-center sticky right-0 z-10 bg-white !bg-white border-l border-slate-200 align-middle">
                      <Button
                        size="sm"
                        onClick={() => handleInspectSingle(item)}
                        className="h-6 px-2 text-[11px] bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 font-medium"
                        title="Lập Biên bản kiểm FQC cho sản phẩm này"
                      >
                        <FileCheck2 className="w-3 h-3 mr-1 text-emerald-600" />
                        Lập FQC
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
